/**
 * Centralized Style Configuration — Single Source of Truth
 * All style references across the platform MUST use this config.
 */

import styleRealism from '@/assets/styles/realism.jpg';
import styleBlackwork from '@/assets/styles/blackwork.jpg';
import styleFineLine from '@/assets/styles/fine-line.jpg';
import styleTraditional from '@/assets/styles/traditional.jpg';
import styleJapanese from '@/assets/styles/japanese.jpg';
import styleWatercolor from '@/assets/styles/watercolor.jpg';
import styleDotwork from '@/assets/styles/dotwork.jpg';
import styleNeoTraditional from '@/assets/styles/neo-traditional.jpg';
import styleBlackGrey from '@/assets/styles/black-grey.jpg';
import styleFullColor from '@/assets/styles/full-color.jpg';
import styleTribal from '@/assets/styles/tribal.jpg';
import styleGeometric from '@/assets/styles/geometric.jpg';
import styleLettering from '@/assets/styles/lettering.jpg';
import styleScript from '@/assets/styles/script.jpg';
import styleMandala from '@/assets/styles/mandala.jpg';
import styleIllustrative from '@/assets/styles/illustrative.jpg';
import styleSurrealism from '@/assets/styles/surrealism.jpg';
import styleOrnamental from '@/assets/styles/ornamental.jpg';
import styleMicroRealism from '@/assets/styles/micro-realism.jpg';
import styleTrashPolka from '@/assets/styles/trash-polka.jpg';

export interface StyleConfig {
  /** Database slug / enum value */
  id: string;
  /** i18n key for label, e.g. 'style.realism' */
  labelKey: string;
  /** Show in Premium Showcase on landing page */
  isPremium: boolean;
  /** Globally active — if false, hidden from all new setups */
  isActive: boolean;
  /** Default thumbnail image for visual cards */
  thumbnailPath: string;
}

/** Fallback image mapping for styles without dedicated assets */
const FALLBACK_IMAGES: Record<string, string> = {
  chicano: styleTraditional,
  cyber_sigilism: styleGeometric,
  patchwork: styleIllustrative,
  sketch: styleFineLine,
  abstract: styleSurrealism,
  minimalist: styleFineLine,
  american_traditional: styleTraditional,
  new_school: styleNeoTraditional,
  biomechanical: styleRealism,
  dark_art: styleBlackwork,
  anime: styleIllustrative,
  kawaii: styleIllustrative,
  celtic: styleOrnamental,
  polynesian: styleTribal,
  maori: styleTribal,
  haida: styleTribal,
  etching: styleBlackwork,
  woodcut: styleBlackwork,
  portrait: styleRealism,
  pet_portrait: styleRealism,
  floral: styleFineLine,
  botanical: styleFineLine,
  sacred_geometry: styleGeometric,
  horror: styleBlackwork,
  fantasy: styleIllustrative,
  mythology: styleJapanese,
  sleeve_art: styleJapanese,
  hand_poke: styleDotwork,
  stick_and_poke: styleDotwork,
  negative_space: styleBlackwork,
  white_ink: styleFineLine,
  uv_ink: styleFullColor,
};

/** Default style images — keyed by style id */
export const STYLE_IMAGES: Record<string, string> = {
  realism: styleRealism,
  blackwork: styleBlackwork,
  fine_line: styleFineLine,
  traditional: styleTraditional,
  japanese: styleJapanese,
  watercolor: styleWatercolor,
  dotwork: styleDotwork,
  neo_traditional: styleNeoTraditional,
  black_grey: styleBlackGrey,
  full_color: styleFullColor,
  tribal: styleTribal,
  geometric: styleGeometric,
  lettering: styleLettering,
  script: styleScript,
  mandala: styleMandala,
  illustrative: styleIllustrative,
  surrealism: styleSurrealism,
  ornamental: styleOrnamental,
  micro_realism: styleMicroRealism,
  trash_polka: styleTrashPolka,
  // New styles use fallbacks
  ...FALLBACK_IMAGES,
  other: styleBlackGrey, // fallback
};

/**
 * Get the image for a style, with optional custom artist thumbnails.
 * Falls back to default STYLE_IMAGES if no custom thumbnail exists.
 */
export function getStyleImage(styleId: string, customThumbnails?: Record<string, string> | null): string {
  if (customThumbnails && customThumbnails[styleId]) {
    return customThumbnails[styleId];
  }
  return STYLE_IMAGES[styleId] || STYLE_IMAGES.other;
}

/**
 * Master list of all tattoo styles.
 * Order here determines display order across the platform.
 */
export const STYLE_REGISTRY: StyleConfig[] = [
  // Premium / Popular styles
  { id: 'realism', labelKey: 'style.realism', isPremium: true, isActive: true, thumbnailPath: styleRealism },
  { id: 'blackwork', labelKey: 'style.blackwork', isPremium: true, isActive: true, thumbnailPath: styleBlackwork },
  { id: 'fine_line', labelKey: 'style.fine_line', isPremium: true, isActive: true, thumbnailPath: styleFineLine },
  { id: 'traditional', labelKey: 'style.traditional', isPremium: true, isActive: true, thumbnailPath: styleTraditional },
  { id: 'japanese', labelKey: 'style.japanese', isPremium: true, isActive: true, thumbnailPath: styleJapanese },
  { id: 'watercolor', labelKey: 'style.watercolor', isPremium: true, isActive: true, thumbnailPath: styleWatercolor },
  { id: 'dotwork', labelKey: 'style.dotwork', isPremium: true, isActive: true, thumbnailPath: styleDotwork },
  { id: 'neo_traditional', labelKey: 'style.neo_traditional', isPremium: true, isActive: true, thumbnailPath: styleNeoTraditional },
  // Standard styles with dedicated assets
  { id: 'black_grey', labelKey: 'style.black_grey', isPremium: false, isActive: true, thumbnailPath: styleBlackGrey },
  { id: 'full_color', labelKey: 'style.full_color', isPremium: false, isActive: true, thumbnailPath: styleFullColor },
  { id: 'tribal', labelKey: 'style.tribal', isPremium: false, isActive: true, thumbnailPath: styleTribal },
  { id: 'geometric', labelKey: 'style.geometric', isPremium: false, isActive: true, thumbnailPath: styleGeometric },
  { id: 'lettering', labelKey: 'style.lettering', isPremium: false, isActive: true, thumbnailPath: styleLettering },
  { id: 'script', labelKey: 'style.script', isPremium: false, isActive: true, thumbnailPath: styleScript },
  { id: 'mandala', labelKey: 'style.mandala', isPremium: false, isActive: true, thumbnailPath: styleMandala },
  { id: 'illustrative', labelKey: 'style.illustrative', isPremium: false, isActive: true, thumbnailPath: styleIllustrative },
  { id: 'surrealism', labelKey: 'style.surrealism', isPremium: false, isActive: true, thumbnailPath: styleSurrealism },
  { id: 'ornamental', labelKey: 'style.ornamental', isPremium: false, isActive: true, thumbnailPath: styleOrnamental },
  { id: 'micro_realism', labelKey: 'style.micro_realism', isPremium: false, isActive: true, thumbnailPath: styleMicroRealism },
  { id: 'trash_polka', labelKey: 'style.trash_polka', isPremium: false, isActive: true, thumbnailPath: styleTrashPolka },
  // New expanded styles (fallback images)
  { id: 'chicano', labelKey: 'style.chicano', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.chicano },
  { id: 'cyber_sigilism', labelKey: 'style.cyber_sigilism', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.cyber_sigilism },
  { id: 'patchwork', labelKey: 'style.patchwork', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.patchwork },
  { id: 'sketch', labelKey: 'style.sketch', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.sketch },
  { id: 'abstract', labelKey: 'style.abstract', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.abstract },
  { id: 'minimalist', labelKey: 'style.minimalist', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.minimalist },
  { id: 'american_traditional', labelKey: 'style.american_traditional', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.american_traditional },
  { id: 'new_school', labelKey: 'style.new_school', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.new_school },
  { id: 'biomechanical', labelKey: 'style.biomechanical', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.biomechanical },
  { id: 'dark_art', labelKey: 'style.dark_art', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.dark_art },
  { id: 'anime', labelKey: 'style.anime', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.anime },
  { id: 'kawaii', labelKey: 'style.kawaii', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.kawaii },
  { id: 'celtic', labelKey: 'style.celtic', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.celtic },
  { id: 'polynesian', labelKey: 'style.polynesian', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.polynesian },
  { id: 'maori', labelKey: 'style.maori', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.maori },
  { id: 'haida', labelKey: 'style.haida', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.haida },
  { id: 'etching', labelKey: 'style.etching', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.etching },
  { id: 'woodcut', labelKey: 'style.woodcut', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.woodcut },
  { id: 'portrait', labelKey: 'style.portrait', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.portrait },
  { id: 'pet_portrait', labelKey: 'style.pet_portrait', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.pet_portrait },
  { id: 'floral', labelKey: 'style.floral', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.floral },
  { id: 'botanical', labelKey: 'style.botanical', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.botanical },
  { id: 'sacred_geometry', labelKey: 'style.sacred_geometry', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.sacred_geometry },
  { id: 'horror', labelKey: 'style.horror', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.horror },
  { id: 'fantasy', labelKey: 'style.fantasy', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.fantasy },
  { id: 'mythology', labelKey: 'style.mythology', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.mythology },
  { id: 'sleeve_art', labelKey: 'style.sleeve_art', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.sleeve_art },
  { id: 'hand_poke', labelKey: 'style.hand_poke', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.hand_poke },
  { id: 'stick_and_poke', labelKey: 'style.stick_and_poke', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.stick_and_poke },
  { id: 'negative_space', labelKey: 'style.negative_space', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.negative_space },
  { id: 'white_ink', labelKey: 'style.white_ink', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.white_ink },
  { id: 'uv_ink', labelKey: 'style.uv_ink', isPremium: false, isActive: true, thumbnailPath: FALLBACK_IMAGES.uv_ink },
  { id: 'other', labelKey: 'style.other', isPremium: false, isActive: true, thumbnailPath: styleBlackGrey },
];

/** Get only globally active styles */
export function getActiveStyles(): StyleConfig[] {
  return STYLE_REGISTRY.filter(s => s.isActive);
}

/** Get premium styles for showcase */
export function getPremiumStyles(): StyleConfig[] {
  return STYLE_REGISTRY.filter(s => s.isPremium && s.isActive);
}

/** Find a style by id */
export function getStyleById(id: string): StyleConfig | undefined {
  return STYLE_REGISTRY.find(s => s.id === id);
}

/** Get all style IDs */
export function getAllStyleIds(): string[] {
  return STYLE_REGISTRY.map(s => s.id);
}
